import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Stack,
  IconButton,
  Divider,
  Container,
  styled,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';

import VerticalTabs from '/packages/shared-components/VerticalTabs/VerticalTabs';
import VerticalTab from '/packages/shared-components/VerticalTab/VerticalTab';
import VerticalTabPanel from '/packages/shared-components/VerticalTabPanel/VerticalTabPanel';
import { cleanInput, pick } from '/packages/shared-components/utils/util';
import { FlashMessageContext } from '/packages/shared-components/utils/flashMessage';
import TestPlanPropertiesForm from '../../../forms/TestPlanProperties/TestPlanPropertiesForm';
import SetSelectionList from '../../../forms/set-selection/SetSelectionList/SetSelectionList';
import { TestSet } from '../../../forms/set-selection/SetSelectionListItem/SetSelectionListItem';
import StickyHeaderV2 from '/packages/shared-components/StickyHeader/StickyHeaderV2';
import { GlobalSearchV2 } from '/packages/shared-components/GlobalSearch/GlobalSearch';
import StatusFormChip from '/packages/shared-components/StatusFormChip/StatusFormChip';
import { useModal } from '/packages/shared-components/utils/modal';
import UnsavedChangesModal from '~src/components/account/management/UnsavedChangesModal/UnsavedChangesModal';

import { GET_API_TEST_PLANS } from '~src/queries/codegen/getApiTestPlans';
import CREATE_API_TEST_PLAN from '~src/queries/codegen/createApiTestPlan';
import GET_API_TEST_PLATFORMS from '~src/queries/codegen/getApiTestPlatform';
import CREATE_API_TEST_PLAN_FROM_GOLD_STANDARD from '~src/queries/codegen/createApiTestPlanFromGoldStandard';
import { CreateAPITestPlanFromGoldStandardInput, GoldStandardCategory } from '~/src/generated/portal/graphql';
import { GET_SKUS_BY_PROVIDERS } from '~src/queries/codegen/getSkusByProviders';
import { GOLD_STANDARD_CATEGORY_MAPPING } from '../GoldStandardSetCard/GoldStandardSetCard';
import RemoveSetModal from '../../RemoveSetModal/RemoveSetModal';

import {
  TestPlanActionsModalProps,
  SkuBoxStates,
  TCreateTestPlanForm,
  createDefaultValues,
} from '../TestPlanActionsModal.types';
import { getSkuValue, validateSkuBox } from '../validation.utils';
import TestSetsTab from '../TestSetsTab/TestSetsTab';
import SkuBox from '../SkuBox/SkuBox';
import Snackbar from '/packages/shared-components/modal/Snackbar/Snackbar';

const Layout = styled(Container)({
  display: 'grid',
  gridTemplateColumns: '305px 1fr',
  columnGap: '20px',
});

export { createDefaultValues as defaultValues };

const TestPlanActionsModal: React.FC<TestPlanActionsModalProps> = ({
  name,
  description,
  partnerId,
  providerId,
  platform,
  environment,
  action,
  sortBy,
  id,
  minApiVersion,
  onNewTestPlanComplete,
}) => {
  const navigate = useNavigate();
  const { setOpen, setContent, handleModalClose } = useModal();
  const { showSuccessMessage } = useContext(FlashMessageContext);

  const methods = useForm<TCreateTestPlanForm>({
    defaultValues: createDefaultValues(partnerId, name, description, providerId, platform, environment, minApiVersion),
  });
  const {
    getValues,
    trigger,
    control,
    watch,
    setValue,
    setError: setFormError,
    clearErrors,
    formState: { isDirty },
  } = methods;

  const [tabValue, setTabValue] = useState(0);
  const [testSetsTab, setTestSetsTab] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testSetTotal, setTestSetTotal] = useState(0);
  const [search, setSearch] = useState<string | null>(null);
  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>({});
  const [activeBoxId, setActiveBoxId] = useState<string | null>(null);

  const [enableAllSets, setEnableAllSets] = useState(false);
  const [goldStandardError, setGoldStandardError] = useState(false);
  const [goldStandardFormValues, setGoldStandardFormValues] = useState<any>(null);
  const [additionalSkuBoxes, setAdditionalSkuBoxes] = useState<number[]>([]);
  const [skuCounter, setSkuCounter] = useState(1);
  const [allSkuBoxes, setAllSkuBoxes] = useState<number[]>([0]);
  const [skuBoxStates, setSkuBoxStates] = useState<SkuBoxStates>({
    main: {
      formValues: null,
      enableAllSets: false,
      hasError: false,
      errorMessage: 'At least one set needs to be active.',
    },
  });

  const watchedValues = watch(['name', 'providerId', 'platform', 'environment', 'minApiVersion']);
  const formComplete = !watchedValues.every((value) => value && value.toString().trim() !== '');
  const skuValueWatch = watch('skuValue');
  const hasSkuValue = !!getSkuValue(skuValueWatch);
  const providerIdValue = getValues('providerId');

  const { data, loading: loadingSkus } = useQuery(GET_SKUS_BY_PROVIDERS, {
    variables: { partnerId, providerId: providerIdValue },
    skip: !providerIdValue,
  });

  const [saveApiTestPlan, { loading }] = useMutation(CREATE_API_TEST_PLAN, {
    onCompleted: (data) => {
      if (data.createAPITestPlan && action === 'New' && onNewTestPlanComplete) {
        onNewTestPlanComplete(data.createAPITestPlan.id);
      }
      showSuccessMessage(
        <>
          <Link to={`/app/partner/${partnerId}/api/test-plans/${data.createAPITestPlan?.id}`}>
            {data.createAPITestPlan?.name}
          </Link>{' '}
          has been successfully {action === 'New' ? 'created' : 'updated'}.
        </>,
        5000,
      );
    },
    refetchQueries: [GET_API_TEST_PLANS, GET_API_TEST_PLATFORMS],
    awaitRefetchQueries: true,
    onError: (error) => {
      setError(error.message);
      setFormError('submissionError', { type: 'manual' });
    },
  });

  const [saveApiTestPlanFromGoldStandard, { loading: loadGoldStandardTestPlan }] = useMutation(
    CREATE_API_TEST_PLAN_FROM_GOLD_STANDARD,
    {
      onCompleted: (data) => {
        if (data.createAPITestPlanFromGoldStandard && action === 'New' && onNewTestPlanComplete) {
          onNewTestPlanComplete(data.createAPITestPlanFromGoldStandard.id);
        }
        showSuccessMessage(
          <>
            <Link to={`/app/partner/${partnerId}/api/test-plans/${data.createAPITestPlanFromGoldStandard?.id}`}>
              {data.createAPITestPlanFromGoldStandard?.name}
            </Link>{' '}
            has been successfully {action === 'New' ? 'created' : 'updated'}.
          </>,
          5000,
        );
      },
      refetchQueries: [
        { query: GET_API_TEST_PLANS, variables: { partnerId, sortBy } },
        { query: GET_API_TEST_PLATFORMS },
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        setError(error.message);
        setFormError('submissionError', { type: 'manual' });
      },
    },
  );

  useLayoutEffect(() => {
    window.scrollTo({ top: 0 });
  }, [tabValue]);

  useEffect(() => {
    if (skuValueWatch) clearErrors('skuValue');
    if (!hasSkuValue) {
      setGoldStandardError(false);
      setEnableAllSets(false);
    }
  }, [skuValueWatch, hasSkuValue, clearErrors]);

  const additionalSkuValuesWatch = watch(additionalSkuBoxes.map((id) => `sku_${id}`));
  useEffect(() => {
    additionalSkuBoxes.forEach((skuId, index) => {
      if (additionalSkuValuesWatch?.[index]) clearErrors(`sku_${skuId}`);
    });
  }, [additionalSkuValuesWatch, additionalSkuBoxes, clearErrors]);

  const validateAllSkuBoxesOnSubmit = async (): Promise<boolean> => {
    if (tabValue !== 1) return false;

    const newSkuBoxStates: SkuBoxStates = { ...skuBoxStates };
    let hasAnyError = false;

    const mainBoxExists = allSkuBoxes.includes(0);
    const skuFieldsToValidate = [
      ...(mainBoxExists ? ['skuValue'] : []),
      ...additionalSkuBoxes.map((id) => `sku_${id}`),
    ];
    const autocompleteValid = await trigger(skuFieldsToValidate);
    if (!autocompleteValid) hasAnyError = true;

    if (mainBoxExists) {
      const mainSkuValue = getValues('skuValue');
      const mainValidation = validateSkuBox(mainSkuValue, goldStandardFormValues);
      newSkuBoxStates.main = {
        ...newSkuBoxStates.main,
        hasError: mainValidation.hasError,
        errorMessage: mainValidation.errorMessage,
      };
      if (mainValidation.hasError) {
        hasAnyError = true;
        setGoldStandardError(true);
      } else {
        setGoldStandardError(false);
      }
    } else {
      setGoldStandardError(false);
    }

    for (const skuId of additionalSkuBoxes) {
      const skuValue = getValues(`sku_${skuId}`);
      const boxState = skuBoxStates[`sku_${skuId}`];
      const validation = validateSkuBox(skuValue, boxState?.formValues);
      newSkuBoxStates[`sku_${skuId}`] = {
        ...newSkuBoxStates[`sku_${skuId}`],
        hasError: validation.hasError,
        errorMessage: validation.errorMessage,
      };
      if (validation.hasError) hasAnyError = true;
    }

    setSkuBoxStates(newSkuBoxStates);
    if (hasAnyError) setFormError('skuValidationError', { type: 'manual', message: 'SKU validation failed' });

    return hasAnyError;
  };

  const clearSubmitError = () => clearErrors('skuValidationError');

  const handleBack = () => {
    if (tabValue === 0) {
      if (isDirty) {
        setContent(
          <UnsavedChangesModal
            handleModalClose={handleModalClose}
            onDiscard={() => {
              navigate(`/app/partner/${partnerId}/api/test-plans`);
              handleModalClose();
            }}
          />,
        );
        setOpen(true);
      } else navigate(`/app/partner/${partnerId}/api/test-plans`);
    } else {
      setTabValue(0);
    }
  };

  const handleSetsChange = (data: TestSet[]) => {
    clearErrors('testSetIds');
    setValue(
      'testSetIds',
      data.flatMap((el) => (el.quantity ? new Array(el.quantity).fill(el.id) : [])),
    );
  };

  const validateAndContinue = async () => {
    const valid = await trigger(['name', 'description', 'providerId', 'platform', 'environment', 'minApiVersion']);
    if (valid && !formComplete) {
      setTestSetsTab(true);
      setTabValue(getValues('planType') === 'auto' ? 1 : 2);
    }
  };

  const handleCancelRequest = () => {
    if (isDirty) {
      setContent(
        <UnsavedChangesModal
          handleModalClose={handleModalClose}
          onDiscard={() => {
            navigate(-1);
            handleModalClose();
          }}
        />,
      );
      setOpen(true);
    } else navigate(`/app/partner/${partnerId}/api/test-plans`);
  };

  const handleAddSku = () => {
    const newSkuId = skuCounter;
    setSkuCounter((prev) => prev + 1);
    setAdditionalSkuBoxes((prev) => [...prev, newSkuId]);
    setAllSkuBoxes((prev) => [...prev, newSkuId]);
    setSkuBoxStates((prev) => ({
      ...prev,
      [`sku_${newSkuId}`]: {
        formValues: null,
        enableAllSets: false,
        hasError: false,
        errorMessage: 'At least one set needs to be active.',
      },
    }));
    setActiveBoxId(`sku_${newSkuId}`);
    setValue('skuAdded', true, { shouldDirty: true });
  };

  const handleRemoveModal = (skuId: number) => {
    setContent(
      <RemoveSetModal
        handleModalClose={handleModalClose}
        onDiscard={() => {
          handleRemoveSku(skuId);
          handleModalClose();
        }}
      />,
    );
    setOpen(true);
  };

  const handleRemoveSku = (skuId: number) => {
    if (allSkuBoxes.length <= 1) return;

    setAdditionalSkuBoxes((prev) => prev.filter((id) => id !== skuId));
    setAllSkuBoxes((prev) => prev.filter((id) => id !== skuId));
    setSkuBoxStates((prev) => {
      const newState = { ...prev };
      if (skuId === 0) delete newState.main;
      else delete newState[`sku_${skuId}`];
      Object.keys(newState).forEach((key) => {
        newState[key] = { ...newState[key], hasError: false, errorMessage: '' };
      });
      return newState;
    });

    if (skuId === 0) {
      setValue('skuValue', undefined);
      setGoldStandardFormValues(null);
      setEnableAllSets(false);
      setGoldStandardError(false);
    } else {
      setValue(`sku_${skuId}`, '');
    }

    setGoldStandardError(false);
    clearErrors('skuValidationError');
    showSuccessMessage('Test Set successfully removed.');
  };

  const collapseAll = () => {
    const collapsed: Record<string, boolean> = {};
    allSkuBoxes.forEach((id) => {
      collapsed[id === 0 ? 'main' : `sku_${id}`] = true;
    });
    setCollapsedState(collapsed);
    setActiveBoxId(null);
  };

  const handleNext = () => {
    if (tabValue === 2) {
      const formValues = getValues();

      if (!formValues.testSetIds || formValues.testSetIds.length === 0) {
        setFormError('testSetIds', { type: 'manual', message: 'At least one set needs to be added.' });
        return;
      }

      const input = cleanInput(formValues);

      return saveApiTestPlan({
        variables: {
          input: pick(input, [
            'description',
            'environment',
            'minApiVersion',
            'name',
            'partnerId',
            'platform',
            'providerId',
            'testSetIds',
          ]),
        },
      });
    } else if (tabValue === 1) {
      clearErrors('skuValidationError');
      clearErrors('submissionError');
      setSkuBoxStates((prev) => {
        const newState = { ...prev };
        Object.keys(newState).forEach((key) => {
          newState[key] = { ...newState[key], hasError: false, errorMessage: '' };
        });
        return newState;
      });
      setGoldStandardError(false);

      const input = getValues();
      const { skuValue, testSetIds, ...rest } = input;
      const filteredInput = Object.keys(rest).reduce((acc, key) => {
        if (!key.startsWith('sku_') || key === 'skuValue') acc[key] = rest[key];
        return acc;
      }, {} as any);

      const apiInputWithoutSku = cleanInput({
        ...filteredInput,
        planType: undefined,
        reconciliation: undefined,
        skuAdded: undefined,
      });

      const mainBaseSku = getSkuValue(skuValue);
      const mainSkuTestScopes =
        goldStandardFormValues && mainBaseSku
          ? [
              {
                baseSku: mainBaseSku,
                testSets: Object.entries(goldStandardFormValues)
                  .filter(([, value]: [string, any]) =>
                    typeof value === 'boolean'
                      ? value
                      : value?.offerTransition || value?.tierDowngrade || value?.tierUpgrade,
                  )
                  .map(([category, value]: [string, any]) => ({
                    category: GOLD_STANDARD_CATEGORY_MAPPING[category],
                    ...(value?.transition1 && { transitionSku1: value.transition1 }),
                    ...(value?.transition2 && { transitionSku2: value.transition2 }),
                  })),
              },
            ]
          : [];

      const additionalSkuTestScopes = additionalSkuBoxes
        .map((skuId) => {
          const additionalSkuValue = getValues(`sku_${skuId}`);
          const boxState = skuBoxStates[`sku_${skuId}`];
          const additionalBaseSku = getSkuValue(additionalSkuValue);
          if (!additionalBaseSku || !boxState?.formValues) return null;
          return {
            baseSku: additionalBaseSku,
            testSets: Object.entries(boxState.formValues)
              .filter(([, value]: [string, any]) =>
                typeof value === 'boolean'
                  ? value
                  : value?.offerTransition || value?.tierDowngrade || value?.tierUpgrade,
              )
              .map(([category, value]: [string, any]) => ({
                category: GOLD_STANDARD_CATEGORY_MAPPING[category],
                ...(value?.transition1 && { transitionSku1: value.transition1 }),
                ...(value?.transition2 && { transitionSku2: value.transition2 }),
              })),
          };
        })
        .filter(Boolean);

      saveApiTestPlanFromGoldStandard({
        variables: {
          input: {
            ...apiInputWithoutSku,
            globalTestSets: getValues('globalTestSets') || [],
            skuTestScopes: [...mainSkuTestScopes, ...additionalSkuTestScopes],
          } as CreateAPITestPlanFromGoldStandardInput,
        },
      });
    } else {
      validateAndContinue();
    }
  };

  const handleCreatePlan = async () => {
    if (tabValue === 0) {
      validateAndContinue();
      return;
    }

    if (tabValue === 1) {
      const hasErrors = await validateAllSkuBoxesOnSubmit();
      if (hasErrors) {
        setTimeout(() => {
          document
            .querySelector('[data-testid="error-snackbar"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }
      handleNext();
      return;
    }

    if (tabValue === 2) {
      handleNext();
      return;
    }
  };

  return (
    <Box>
      <StickyHeaderV2>
        <Container disableGutters maxWidth="lg" sx={{ pt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton onClick={handleBack}>
              <ArrowBackIcon fontSize="small" color="primary" />
            </IconButton>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleCancelRequest}>
                Cancel Request
              </Button>
              <Button
                variant="contained"
                onClick={handleCreatePlan}
                disabled={loading || loadGoldStandardTestPlan}
                data-cy="create-plan-btn"
              >
                Create Plan
              </Button>
            </Stack>
          </Stack>
        </Container>
        <Divider sx={{ mt: 2 }} />
      </StickyHeaderV2>

      <FormProvider {...methods}>
        <Layout maxWidth="lg" disableGutters sx={{ py: 4 }}>
          <VerticalTabs>
            <VerticalTab
              title="Plan Properties"
              value={0}
              selectedValue={tabValue}
              onClick={setTabValue}
              icon={
                <StatusFormChip
                  control={control}
                  fields={['name', 'providerId', 'platform', 'minApiVersion']}
                  requiredFields={['name', 'providerId', 'platform', 'minApiVersion']}
                />
              }
            />
            <TestSetsTab
              selectedValue={tabValue}
              onClick={setTabValue}
              control={control}
              enabled={testSetsTab}
              goldStandardFormValues={goldStandardFormValues}
              hasSkuValue={hasSkuValue}
              goldStandardError={goldStandardError}
              skuBoxStates={skuBoxStates}
              additionalSkuBoxes={additionalSkuBoxes}
            />
          </VerticalTabs>

          <Box>
            <VerticalTabPanel value={0} selectedValue={tabValue} sx={{ maxWidth: '630px', paddingLeft: 0 }}>
              <TestPlanPropertiesForm partnerId={partnerId} modal nextStep={validateAndContinue} />
            </VerticalTabPanel>

            <VerticalTabPanel value={1} selectedValue={tabValue} sx={{ pl: 0 }}>
              <Stack gap={3}>
                <Stack gap={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Gold Standard Test Sets</Typography>
                    <IconButton onClick={collapseAll}>
                      <UnfoldLessOutlinedIcon fontSize="xs" sx={{ color: 'text.primary', cursor: 'pointer' }} />
                    </IconButton>
                  </Stack>
                  <Typography variant="body1" color="text.secondary">
                    Gold standard test sets are predefined groups of cases used as a baseline to ensure consistency and
                    reliability in the results. Select the SKUs and assign them to the test sets to be executed.
                  </Typography>
                </Stack>

                {allSkuBoxes.map((skuId) => (
                  <SkuBox
                    key={skuId === 0 ? 'main' : skuId}
                    skuId={skuId}
                    skuFieldName={skuId === 0 ? 'skuValue' : `sku_${skuId}`}
                    control={control}
                    watch={watch}
                    data={data}
                    skuBoxStates={skuBoxStates}
                    setSkuBoxStates={setSkuBoxStates}
                    goldStandardFormValues={skuId === 0 ? goldStandardFormValues : undefined}
                    setGoldStandardFormValues={skuId === 0 ? setGoldStandardFormValues : undefined}
                    enableAllSets={skuId === 0 ? enableAllSets : undefined}
                    setEnableAllSets={skuId === 0 ? setEnableAllSets : undefined}
                    goldStandardError={skuId === 0 ? goldStandardError : undefined}
                    setGoldStandardError={skuId === 0 ? setGoldStandardError : undefined}
                    allSkuBoxes={allSkuBoxes}
                    onRemove={() => handleRemoveModal(skuId)}
                    activeBoxId={activeBoxId}
                    setActiveBoxId={setActiveBoxId}
                    collapsedState={collapsedState}
                    setCollapsedState={setCollapsedState}
                    onUserInteraction={clearSubmitError}
                    loadingSkus={loadingSkus}
                  />
                ))}

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Button
                    variant="text"
                    size="small"
                    sx={{ flexShrink: 0 }}
                    startIcon={<AddIcon />}
                    onClick={handleAddSku}
                    name="additionalSkus"
                  >
                    Add SKU
                  </Button>
                </Stack>

                <Divider />

                <Stack gap={3}>
                  <Stack gap={1.5}>
                    <Typography variant="subtitle1">Reconciliation Test Sets</Typography>
                    <Typography variant="body1" color="text.secondary">
                      This test set validates subscription data consistency. By enabling it, the reconciliation process
                      will compare the test subscriptions used during certification against the expected records,
                      ensuring that discrepancies can be identified and corrected.
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        padding: '16px',
                      }}
                    >
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body1" color="text.primary" width="256px">
                          Reconciliation
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Controller
                            name="globalTestSets"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{ color: 'text.primary' }}
                                control={
                                  <Switch
                                    color="success"
                                    checked={(field.value || []).some(
                                      (set: any) => set.category === GoldStandardCategory.Reconciliation,
                                    )}
                                    onChange={(e) => {
                                      const currentSets = field.value || [];
                                      const newValue = e.target.checked
                                        ? [...currentSets, { category: GoldStandardCategory.Reconciliation }]
                                        : currentSets.filter(
                                            (set: any) => set.category !== GoldStandardCategory.Reconciliation,
                                          );
                                      field.onChange(newValue);
                                    }}
                                  />
                                }
                                labelPlacement="start"
                                label=""
                              />
                            )}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </VerticalTabPanel>

            <VerticalTabPanel value={2} selectedValue={tabValue} sx={{ maxWidth: '630px', pl: 0 }}>
              <Stack gap={3}>
                <Stack gap={1.5}>
                  <Typography variant="h6">Test Sets</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Test sets are groups of cases designed to verify the integration. Select or remove the ones you
                    want.
                  </Typography>
                </Stack>
                <GlobalSearchV2
                  id="search"
                  label="Search by set name"
                  filterState={search}
                  onChange={setSearch}
                  auto
                  hideActionButton
                  size="medium"
                />
                <Controller
                  control={control}
                  name="testSetIds"
                  rules={{ required: true }}
                  render={({ fieldState }) => (
                    <>
                      {fieldState.error && (
                        <Snackbar variant="subtle" status="error" content={fieldState.error.message} />
                      )}
                      <SetSelectionList
                        onSetsChange={handleSetsChange}
                        testSetTotal={testSetTotal}
                        setTestSetTotal={setTestSetTotal}
                        testPlanId={id}
                        search={search}
                      />
                    </>
                  )}
                />
              </Stack>
            </VerticalTabPanel>

            {error && (
              <Box sx={{ mt: 2 }}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
          </Box>
        </Layout>
      </FormProvider>
    </Box>
  );
};

export default TestPlanActionsModal;
